import { useEffect, useRef, useState } from 'react';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useWebsiteEditorDialogContext } from '@website-editor/context/WebsiteEditorDialogContext';
import { ACCOMMODATIONS_DIALOGS } from '../../Accommodations.dialogs';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useHasSeenOnboardingVenueDialog } from '@shared/utils/accommodations';
import type { EventVenueState, UseEventVenueDialogReturn } from './EventVenueDialog.types';

const VENUE_KEY = 'venue';

/**
 * Hook for managing event venue dialog state.
 *
 * Simplified approach (TRAV-2305):
 * - Dialog opens ONLY when redirected from dashboard venue menu (venue=true param)
 * - Previous complex onboarding flows removed, since we now have the accommodations tour with add venue button
 * - Tour dialog now handles auto-population onboarding (see StartTourButton)
 */
export const useEventVenueDialog = (): UseEventVenueDialogReturn => {
  const [eventVenueState, setEventVenueState] = useState<EventVenueState>('create');
  const hasInitializedRef = useRef(false);

  const updateSearchParam = useUpdateSearchParam();
  const queryParamHelper = useQueryParamHelper();
  const { registerDialog, dismiss, isOpen, requestShow } = useWebsiteEditorDialogContext(ACCOMMODATIONS_DIALOGS.VENUE_DIALOG);

  const { setHasSeenOnboardingVenueDialog } = useHasSeenOnboardingVenueDialog();

  const { venueInfo, loading: loadingVenueInfo } = useEventPlaces();

  // Capture URL redirect param once on mount - this is intentionally not reactive
  const isDashboardRedirectRef = useRef<boolean | null>(null);
  if (isDashboardRedirectRef.current === null) {
    isDashboardRedirectRef.current = queryParamHelper.getValueString(VENUE_KEY) === 'true';
  }
  const isDashboardRedirect = isDashboardRedirectRef.current;

  useEffect(() => {
    if (loadingVenueInfo) {
      registerDialog('loading');
      return;
    }

    setEventVenueState(venueInfo?.hasVenue ? 'update' : 'create');

    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    registerDialog(isDashboardRedirect ? 'ready' : 'skip');
    if (isDashboardRedirect) {
      setHasSeenOnboardingVenueDialog();
      updateSearchParam(VENUE_KEY, null, true);
    }
  }, [loadingVenueInfo, venueInfo, isDashboardRedirect, registerDialog, setHasSeenOnboardingVenueDialog, updateSearchParam]);

  const handleClose = useEventCallback(() => {
    dismiss();
  });

  // Always an explicit user action (venue title / Add Venue / tour "show hotel picks"), so it must win even over an open tooltip.
  const requestOpen = useEventCallback(() => {
    requestShow({ force: true });
  });

  return {
    venueInfo,
    eventVenueState,
    setEventVenueState,
    isOpen,
    onClose: handleClose,
    requestOpen,
    isDashboardRedirect,
  };
};
